/**
 * ScrollIndicators - Componente para indicar scroll horizontal disponível
 * 
 * Exibe gradientes fade e setas de navegação nas bordas quando há conteúdo
 * oculto à esquerda ou direita do container de scroll.
 */

import { useState, useEffect, useRef, useCallback, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ScrollIndicatorsProps {
  children: ReactNode;
  /** Exibir setas de navegação clicáveis */
  showArrows?: boolean;
  /** Exibir gradientes fade nas bordas */
  showGradients?: boolean;
  /** Largura do gradiente (classe Tailwind) */
  gradientWidth?: string;
  /** Quantidade de pixels por clique nas setas */
  scrollAmount?: number;
  /** Classes adicionais para o container */
  className?: string;
  /**
   * Renderiza SEMPRE as duas setas, aplicando estado visual desabilitado
   * quando não há mais scroll disponível naquele sentido.
   * Preserva o comportamento padrão (setas condicionais) quando falso.
   */
  alwaysShowArrows?: boolean;
}

export function ScrollIndicators({
  children,
  showArrows = true,
  showGradients = true,
  gradientWidth = 'w-12',
  scrollAmount = 300,
  className,
  alwaysShowArrows = false,
}: ScrollIndicatorsProps) {
  // Estados para controlar visibilidade dos indicadores
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Referência para o container externo
  const containerRef = useRef<HTMLDivElement>(null);
  // Referência para o viewport atualmente monitorado (pode mudar em runtime)
  const viewportRef = useRef<HTMLElement | null>(null);
  // Referência aos observers/listeners ativos para cleanup correto ao reatar
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  // Guarda o handler de scroll atual para permitir remoção limpa
  const scrollHandlerRef = useRef<(() => void) | null>(null);

  // Localiza o viewport dentro do container (Radix ou nativo com data-scroll-viewport)
  const findViewport = useCallback((): HTMLElement | null => {
    const container = containerRef.current;
    if (!container) return null;

    const candidates = Array.from(
      container.querySelectorAll<HTMLElement>(
        '[data-radix-scroll-area-viewport], [data-scroll-viewport]',
      ),
    );

    // Preferimos o elemento que realmente possui overflow horizontal.
    // Isso evita anexar as setas em wrappers visuais quando a rolagem real
    // está em um wrapper interno criado por componentes como Table/ScrollArea.
    return (
      candidates.find(
        (candidate) => candidate.scrollWidth > candidate.clientWidth + 2,
      ) ??
      candidates[0] ??
      null
    );
  }, []);

  // Calcula estados de scroll com base no viewport atual
  const checkScroll = useCallback(() => {
    const scrollViewport = viewportRef.current;
    if (!scrollViewport) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = scrollViewport;
    const hasHorizontalOverflow = scrollWidth > clientWidth + 2;

    if (!hasHorizontalOverflow) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    // Margem de tolerância (2px) para evitar flickering em bordas
    setCanScrollLeft(scrollLeft > 2);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 2);
  }, []);

  // Scroll suave para direção especificada
  const scrollTo = useCallback(
    (direction: 'left' | 'right') => {
      const scrollViewport = viewportRef.current;
      if (!scrollViewport) return;
      const amount = direction === 'left' ? -scrollAmount : scrollAmount;
      scrollViewport.scrollBy({ left: amount, behavior: 'smooth' });

      // Recalcula após iniciar e concluir a animação suave, mantendo o estado
      // das setas coerente mesmo em navegadores que emitem poucos eventos scroll.
      requestAnimationFrame(checkScroll);
      window.setTimeout(checkScroll, 350);
    },
    [scrollAmount, checkScroll],
  );

  // Anexa/reata listeners e observers ao viewport atual.
  // Chamado sempre que o MutationObserver detecta uma mudança de DOM ou no mount.
  const attachToViewport = useCallback(() => {
    const nextViewport = findViewport();

    // Nada mudou → nada a fazer
    if (nextViewport === viewportRef.current) {
      // Ainda assim, recalcula (largura pode ter mudado)
      checkScroll();
      return;
    }

    // Desanexa do viewport antigo (se existir)
    const prev = viewportRef.current;
    if (prev && scrollHandlerRef.current) {
      prev.removeEventListener('scroll', scrollHandlerRef.current);
    }
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }

    viewportRef.current = nextViewport;

    if (!nextViewport) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      scrollHandlerRef.current = null;
      return;
    }

    // Anexa listeners no novo viewport
    const handler = () => checkScroll();
    scrollHandlerRef.current = handler;
    nextViewport.addEventListener('scroll', handler, { passive: true });

    const ro = new ResizeObserver(() => checkScroll());
    ro.observe(nextViewport);
    // Observa também o conteúdo interno para detectar mudanças de scrollWidth
    if (nextViewport.firstElementChild) {
      ro.observe(nextViewport.firstElementChild);
    }
    resizeObserverRef.current = ro;

    // Estado inicial
    checkScroll();
  }, [findViewport, checkScroll]);

  // Efeito principal: MutationObserver detecta quando o viewport aparece/muda no DOM
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Tenta anexar imediatamente (caso o viewport já esteja presente)
    attachToViewport();

    // Observa mudanças estruturais dentro do container (loading → tabela, refetch, etc.)
    const mo = new MutationObserver(() => {
      attachToViewport();
    });
    mo.observe(container, { childList: true, subtree: true });

    // Reagir a resize da janela também
    const onWindowResize = () => checkScroll();
    window.addEventListener('resize', onWindowResize);

    return () => {
      mo.disconnect();
      window.removeEventListener('resize', onWindowResize);
      if (viewportRef.current && scrollHandlerRef.current) {
        viewportRef.current.removeEventListener('scroll', scrollHandlerRef.current);
      }
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      viewportRef.current = null;
      scrollHandlerRef.current = null;
    };
  }, [attachToViewport, checkScroll]);

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Gradiente fade esquerdo */}
      {showGradients && canScrollLeft && (
        <div 
          className={cn(
            'absolute left-0 top-0 bottom-0 pointer-events-none z-10',
            'bg-gradient-to-r from-background via-background/60 to-transparent',
            gradientWidth
          )}
          aria-hidden="true"
        />
      )}

      {/* Gradiente fade direito */}
      {showGradients && canScrollRight && (
        <div 
          className={cn(
            'absolute right-0 top-0 bottom-0 pointer-events-none z-10',
            'bg-gradient-to-l from-background via-background/60 to-transparent',
            gradientWidth
          )}
          aria-hidden="true"
        />
      )}

      {/* Seta de navegação esquerda */}
      {showArrows && (alwaysShowArrows || canScrollLeft) && (
        <button
          type="button"
          onClick={() => scrollTo('left')}
          disabled={!canScrollLeft}
          className={cn(
            'absolute left-2 top-1/2 -translate-y-1/2 z-20',
            'w-8 h-8 rounded-full',
            'bg-background/90 border border-border shadow-md',
            'flex items-center justify-center',
            'transition-all duration-200',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
            canScrollLeft
              ? 'hover:bg-accent hover:scale-110 cursor-pointer'
              : 'opacity-40 cursor-not-allowed'
          )}
          aria-label="Rolar para esquerda"
        >
          <ChevronLeft className="h-4 w-4 text-foreground" />
        </button>
      )}

      {/* Seta de navegação direita */}
      {showArrows && (alwaysShowArrows || canScrollRight) && (
        <button
          type="button"
          onClick={() => scrollTo('right')}
          disabled={!canScrollRight}
          className={cn(
            'absolute right-2 top-1/2 -translate-y-1/2 z-20',
            'w-8 h-8 rounded-full',
            'bg-background/90 border border-border shadow-md',
            'flex items-center justify-center',
            'transition-all duration-200',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
            canScrollRight
              ? 'hover:bg-accent hover:scale-110 cursor-pointer'
              : 'opacity-40 cursor-not-allowed'
          )}
          aria-label="Rolar para direita"
        >
          <ChevronRight className="h-4 w-4 text-foreground" />
        </button>
      )}

      {/* Conteúdo (ScrollArea) */}
      {children}
    </div>
  );
}
